import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Save, Trash2, FileText, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill } from "./OpsDashboard";

const STATUSES = ["pending", "scheduled", "in_progress", "completed", "cancelled"];

export default function OpsOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      setDraft({
        customer: { ...(data.customer || {}) },
        service: data.service || "",
        description: data.description || "",
        scheduled_at: data.scheduled_at ? data.scheduled_at.substring(0, 16) : "",
        assigned_to: data.assigned_to || "",
        status: data.status || "pending",
        price: data.price || 0,
        notes: data.notes || "",
      });
    } catch {
      toast.error("Pedido no encontrado");
      navigate("/admin/ops/orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !draft || !order) {
    return <div className="p-10 text-white/60 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;
  }

  const setCust = (k, v) => setDraft((d) => ({ ...d, customer: { ...d.customer, [k]: v } }));
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...draft,
        scheduled_at: draft.scheduled_at ? new Date(draft.scheduled_at).toISOString() : null,
        price: Number(draft.price) || 0,
      };
      const { data } = await api.patch(`/orders/${id}`, payload);
      setOrder(data);
      toast.success("Pedido guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Eliminar este pedido?")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Eliminado");
      navigate("/admin/ops/orders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const generateInvoice = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/orders/${id}/invoice`);
      toast.success(`Factura ${data.number} generada`);
      navigate(`/admin/ops/invoices/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la factura");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-4xl">
      <Link to="/admin/ops/orders" className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#D4AF37] mb-5">
        <ChevronLeft className="w-3.5 h-3.5" /> Pedidos
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">Pedido</p>
            <StatusPill value={order.status} />
            {order.invoice_id && (
              <Link to={`/admin/ops/invoices/${order.invoice_id}`} className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold inline-flex items-center gap-1 hover:bg-emerald-500/25">
                Ver factura <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{order.customer?.full_name || "Pedido"}</h1>
          <p className="text-white/45 text-xs mt-1">Creado {new Date(order.created_at).toLocaleString()}</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {!order.invoice_id && (
            <button
              onClick={generateInvoice}
              disabled={generating}
              data-testid="order-generate-invoice"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold bg-emerald-500 text-white hover:bg-emerald-400 transition-all disabled:opacity-60"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Generar factura
            </button>
          )}
          <button
            onClick={save}
            disabled={saving}
            data-testid="order-save"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold bg-[#D4AF37] text-[#0A0A0A] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>
      </div>

      {/* Customer */}
      <Section title="Cliente">
        <Row>
          <Field label="Nombre">
            <input type="text" value={draft.customer.full_name || ""} onChange={(e) => setCust("full_name", e.target.value)} className="input-dark" data-testid="order-customer-name" />
          </Field>
          <Field label="Teléfono">
            <input type="tel" value={draft.customer.phone || ""} onChange={(e) => setCust("phone", e.target.value)} className="input-dark" />
          </Field>
        </Row>
        <Row>
          <Field label="Email">
            <input type="email" value={draft.customer.email || ""} onChange={(e) => setCust("email", e.target.value)} className="input-dark" />
          </Field>
          <Field label="Dirección">
            <input type="text" value={draft.customer.address || ""} onChange={(e) => setCust("address", e.target.value)} className="input-dark" />
          </Field>
        </Row>
      </Section>

      {/* Service */}
      <Section title="Servicio">
        <Row>
          <Field label="Servicio">
            <select value={draft.service} onChange={(e) => set("service", e.target.value)} className="input-dark cursor-pointer" data-testid="order-service">
              {["Moving Services","Cleaning Services","Junk Removal","Lock Installation","TV Mounting","Other"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Precio (USD)">
            <input type="number" step="0.01" min="0" value={draft.price} onChange={(e) => set("price", e.target.value)} className="input-dark" data-testid="order-price" />
          </Field>
        </Row>
        <Field label="Descripción / detalles del trabajo">
          <textarea rows={3} value={draft.description} onChange={(e) => set("description", e.target.value)} className="input-dark resize-none" />
        </Field>
      </Section>

      {/* Schedule */}
      <Section title="Programación">
        <Row>
          <Field label="Estado">
            <select value={draft.status} onChange={(e) => set("status", e.target.value)} className="input-dark cursor-pointer" data-testid="order-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Fecha y hora">
            <input type="datetime-local" value={draft.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} className="input-dark" data-testid="order-schedule" />
          </Field>
        </Row>
        <Field label="Asignado a">
          <input type="text" value={draft.assigned_to} onChange={(e) => set("assigned_to", e.target.value)} placeholder="Ej: Juan García + crew B" className="input-dark" data-testid="order-assigned" />
        </Field>
      </Section>

      <Section title="Notas internas">
        <textarea rows={3} value={draft.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notas privadas para el equipo…" className="input-dark resize-none" />
      </Section>

      <div className="mt-8 pt-6 border-t border-white/5 flex">
        <button
          onClick={del}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-all"
        >
          <Trash2 className="w-3.5 h-3.5" /> Eliminar pedido
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 sm:p-6 mb-5">
      <h3 className="font-display text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-white/55 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
